import { createStyles, Grid, makeStyles } from "@material-ui/core";
import { AutocompleteInputChangeReason } from "@material-ui/lab";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Label,
    RelodeApiError,
    RelodeTheme,
    SelectableButtonItem,
    SelectableButtonsList,
    SelectInput,
    Text,
    useDebounce,
    useNotifications,
} from "@relode/core";
import classnames from "classnames";
import * as React from "react";

import { TeamMemberSuggestion } from "../../../../../shared/types";
import {
    AddTeamMemberDialogProps,
    AddTeamMemberState,
    AddTeamMemberStatus,
} from "./addTeamMemberDialogProps";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            downshift: {
                margin: theme.spacing(0, 0, 4, 0),
            },
            section: {
                margin: theme.spacing(2, 0),
            },
            lastSection: {
                marginBottom: 0,
            },
            selectableButtonsList: {
                paddingTop: "6px",
                paddingBottom: "7px",
            },
            menuItemDetails: {
                whiteSpace: "normal",
                height: "auto",
                display: "block",
            },
            menuEmptyMessage: {
                height: "auto",
                whiteSpace: "initial",
            },
        }),
    { name: "AddTeamMemberDialog" }
);

export const AddTeamMemberDialog = (props: AddTeamMemberDialogProps) => {
    const classes = useStyles({});
    const { actions: notifications } = useNotifications();
    const {
        onCancel,
        loadTeamMemberSuggestions,
        onSubmit,
        onComplete,
        suggestions,
        teamMember,
        jobName,
    } = props;

    const [
        selectedTeamMember,
        setSelectedTeamMember,
    ] = React.useState<TeamMemberSuggestion>(undefined);
    const [teamMemberError, setTeamMemberError] = React.useState<string>(
        undefined
    );
    const [selectedUserTypeId, setSelectedUserTypeId] = React.useState<number>(
        undefined
    );
    const [userTypeIdError, setUserTypeIdError] = React.useState<string>(
        undefined
    );
    const [teamMembersSuggestions, setTeamMembersSuggestions] = React.useState<
        TeamMemberSuggestion[]
    >(suggestions);

    const [state, setState] = React.useState<AddTeamMemberState>({
        error: undefined,
        status: "unset",
        isBusy: false,
    });

    const setAddTeamMemberStatus = (
        status: AddTeamMemberStatus,
        error: RelodeApiError | undefined
    ) => {
        setState(o => ({ ...o, status, error, isBusy: status === "pending" }));
    };

    React.useEffect(() => {
        notifications.closeAreaNotification("manageTeamMembers");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications]);

    React.useEffect(() => {
        setTeamMembersSuggestions(suggestions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestions]);

    React.useEffect(() => {
        switch (state.status) {
            // NOTE: DO NOT SET ANY STATE HERE. WILL CAUSE LOOP
            case "pending": {
                notifications.closeAreaNotification("manageTeamMembers");
                break;
            }

            case "failure": {
                if (!state.error) return;

                if (state.error.summary) {
                    notifications.showArea(
                        "error",
                        state.error.summary,
                        "manageTeamMembers",
                        {
                            canClose: false,
                        }
                    );
                }
                break;
            }

            case "success": {
                notifications.closeAreaNotification("manageTeamMembers");
                onComplete();
                break;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    React.useEffect(() => {
        if (teamMember) {
            setSelectedTeamMember({
                fullName: teamMember.fullName,
                userId: teamMember.userId,
                email: teamMember.email,
                teamName: teamMember.teamName,
            });
            setSelectedUserTypeId(teamMember.jobUserTypeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamMember]);

    const loadSuggestions = async (searchText: string) => {
        await loadTeamMemberSuggestions(searchText);
    };

    const debounceSuggestion = useDebounce(loadSuggestions, 500, []);

    const handleInputChange = async (
        event: never,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (!value || value.trim() === "" || value.trim().length === 0) {
            setTeamMembersSuggestions([]);
            return;
        }
        await debounceSuggestion(value);
    };

    const handleSuggestionSelected = (
        e: never,
        newValue: TeamMemberSuggestion
    ) => {
        //this is needed when option is selected by pressing the down arrow and enter
        if (!newValue) {
            setSelectedTeamMember(undefined);
            setTeamMemberError(undefined);
            return;
        }

        if (newValue) {
            setSelectedTeamMember(newValue);
        }
    };

    const handleSubmit = async () => {
        let isValid = true;

        if (!selectedTeamMember) {
            isValid = false;
            setTeamMemberError("user is required");
        }

        if (!selectedUserTypeId) {
            isValid = false;
            setUserTypeIdError("user role is required");
        }

        if (isValid) {
            setAddTeamMemberStatus("pending", undefined);
            await onSubmit(
                selectedTeamMember.userId,
                selectedUserTypeId,
                !!teamMember,
                setAddTeamMemberStatus
            );
        }
    };

    const handleButtonFieldChange = (value: SelectableButtonItem) => {
        setSelectedUserTypeId(value.id);
        setUserTypeIdError(undefined);
    };

    const userTypes = [
        {
            id: 10,
            name: "hiring team manager",
            details:
                "Same permissions as member + seeing desired salary/rate, reject or move the candidate forward, manage team on the job, and message the candidate.",
        },
        {
            id: 60,
            name: "hiring team member",
            details:
                "Can view candidates, provide feedback and message the team and Relode.",
        },
    ];

    const getOptionLabel = (option: TeamMemberSuggestion) =>
        option.fullName ?? "";
    const getOptionDetails = (option: TeamMemberSuggestion) =>
        [option.email, option.teamName].filter(o => !!o).join(" • ");

    return (
        <Dialog open onClose={onCancel}>
            <DialogTitle
                title={(!teamMember ? "add" : "update") + " team member"}
                onClose={onCancel}
            />
            <DialogContent
                isLoading={state.isBusy}
                notificationAreaName="manageTeamMembers"
            >
                <Text variant="h4">
                    {!!teamMember ? teamMember.fullName : null}{" "}
                    {!!teamMember ? <br /> : null} {jobName}
                </Text>
                {!teamMember ? (
                    <div className={classes.section}>
                        <SelectInput
                            mode="suggest"
                            label="search job users"
                            error={!!teamMemberError}
                            helperText={teamMemberError}
                            options={teamMembersSuggestions ?? []}
                            onChange={handleSuggestionSelected}
                            onInputChange={handleInputChange}
                            data-cy="team-member-select"
                            getOptionLabel={getOptionLabel}
                            renderOption={(o, s) => (
                                <Grid container>
                                    <Grid item xs={12}>
                                        {getOptionLabel(o)}
                                    </Grid>
                                    <Grid item xs={12} style={{ fontSize: 14 }}>
                                        {getOptionDetails(o)}
                                    </Grid>
                                </Grid>
                            )}
                        />
                    </div>
                ) : null}

                <div
                    className={classnames(classes.section, classes.lastSection)}
                >
                    <Label label="assign role" fullWidth>
                        <SelectableButtonsList
                            id="userType"
                            className={classes.selectableButtonsList}
                            onClick={handleButtonFieldChange}
                            items={userTypes.map(item => ({
                                id: item.id,
                                label: item.name,
                                details: item.details,
                                selected: selectedUserTypeId === item.id,
                            }))}
                            size="lg"
                            error={!!userTypeIdError}
                            helperText={userTypeIdError}
                        />
                    </Label>
                </div>
            </DialogContent>
            <DialogActions
                submit={{
                    onSubmit: handleSubmit,
                    disabled: state.isBusy,
                    label: !teamMember ? "add" : "save",
                }}
            />
        </Dialog>
    );
};
